import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AuthService from "services/auth";
import {
  getProductReviews,
  getReviewEligibility,
  submitProductReview,
} from "services/reviews";
import { LoyaltyService, ReviewMediaService } from "services/loyalty";
import { MAX_REVIEW_PHOTOS, checkVideo, compressPhoto } from "utils/reviewMedia";
import { useTranslation } from "i18n";
import ReviewSummaryCard from "components/AI/ReviewSummaryCard";

function StarPicker({ value, onChange }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1" role="group" aria-label={t("reviews.form.yourRating")}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`text-2xl transition ${
            n <= value ? "text-amber-500" : "text-slate-300 hover:text-amber-300"
          }`}
          aria-label={t(n > 1 ? "reviews.form.stars" : "reviews.form.star", { count: n })}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function Badge({ children, tone = "success" }) {
  const tones = {
    success: "border-emerald-200 bg-emerald-50 text-emerald-800",
    info: "border-sky-200 bg-sky-50 text-sky-800",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tones[tone]}`}>
      {children}
    </span>
  );
}

function ReportButton({ reviewId }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");

  if (state === "done") {
    return <p className="text-xs text-muted">{t("reviews.report.thanks")}</p>;
  }
  if (!open) {
    return (
      <button
        type="button"
        className="text-xs font-medium text-muted underline-offset-2 hover:underline"
        onClick={() => {
          if (!AuthService.isAuthenticated()) {
            window.dispatchEvent(new CustomEvent("zaynic:open-auth", { detail: { tab: "login" } }));
            return;
          }
          setOpen(true);
        }}
      >
        {t("reviews.report.button")}
      </button>
    );
  }
  const send = async () => {
    setState("sending");
    const res = await ReviewMediaService.report(reviewId, reason.trim());
    if (res.success) setState("done");
    else {
      setError(res.error);
      setState("error");
    }
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={500}
        placeholder={t("reviews.report.reasonPlaceholder")}
        className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900"
      />
      <button
        type="button"
        disabled={state === "sending"}
        onClick={send}
        className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-800 disabled:opacity-60"
      >
        {state === "sending" ? t("common.working") : t("reviews.report.send")}
      </button>
      <button type="button" onClick={() => setOpen(false)} className="text-xs text-muted hover:underline">
        {t("common.cancel")}
      </button>
      {state === "error" && <p className="w-full text-xs text-rose-600">{error}</p>}
    </div>
  );
}

function MediaPicker({ media, setMedia, disabled }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const photos = media.filter((m) => m.kind === "photo");
  const hasVideo = media.some((m) => m.kind === "video");

  const add = async (kind, files) => {
    setError("");
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(true);
    let current = media;
    try {
      for (const file of list) {
        if (kind === "photo" && current.filter((m) => m.kind === "photo").length + 1 > MAX_REVIEW_PHOTOS) {
          setError(t("reviews.media.tooManyPhotos", { max: MAX_REVIEW_PHOTOS }));
          break;
        }
        let ready;
        try {
          ready = kind === "photo" ? await compressPhoto(file) : await checkVideo(file);
        } catch (e) {
          setError(t(e.message));
          break;
        }
        const res = await ReviewMediaService.upload(kind, ready);
        if (!res.success) {
          setError(res.error);
          break;
        }
        const preview = URL.createObjectURL(ready);
        current = [...current, { id: res.data.id, kind, preview }];
        setMedia(current);
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = (id) => setMedia(media.filter((m) => m.id !== id));

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-slate-800">{t("reviews.media.title")}</p>
      <div className="flex flex-wrap gap-2">
        {media.map((m) => (
          <div key={m.id} className="relative h-16 w-16 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
            {m.kind === "photo" ? (
              <img src={m.preview} alt={t("reviews.media.photoAlt")} className="h-full w-full object-cover" />
            ) : (
              <video src={m.preview} className="h-full w-full object-cover" muted />
            )}
            <button
              type="button"
              onClick={() => remove(m.id)}
              aria-label={t("reviews.media.remove")}
              className="absolute end-0.5 top-0.5 rounded-full bg-slate-900/70 px-1.5 text-[11px] font-bold text-white"
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <label className={`cursor-pointer rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 ${
          disabled || busy || photos.length >= MAX_REVIEW_PHOTOS ? "pointer-events-none opacity-50" : ""}`}>
          {t("reviews.media.addPhotos", { count: photos.length, max: MAX_REVIEW_PHOTOS })}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="sr-only"
            data-testid="review-photo-input"
            onChange={(e) => { add("photo", e.target.files); e.target.value = ""; }}
          />
        </label>
        <label className={`cursor-pointer rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 ${
          disabled || busy || hasVideo ? "pointer-events-none opacity-50" : ""}`}>
          {t("reviews.media.addVideo")}
          <input
            type="file"
            accept="video/mp4,video/webm"
            className="sr-only"
            data-testid="review-video-input"
            onChange={(e) => { add("video", e.target.files); e.target.value = ""; }}
          />
        </label>
        {busy && <span className="text-xs text-muted">{t("reviews.media.uploading")}</span>}
      </div>
      <p className="text-[11px] text-muted">{t("reviews.media.help")}</p>
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </div>
  );
}

export default function ProductReviewsSection({
  productId,
  ratingValue,
  reviewCount,
  onReviewSubmitted,
}) {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formRating, setFormRating] = useState(5);
  const [formTitle, setFormTitle] = useState("");
  const [formComment, setFormComment] = useState("");
  const [formError, setFormError] = useState("");
  const [orderItemId, setOrderItemId] = useState("");
  const [media, setMedia] = useState([]);
  const [submittedStatus, setSubmittedStatus] = useState("");
  const [coinsConfig, setCoinsConfig] = useState(null);

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    const [listRes, eligRes] = await Promise.all([
      getProductReviews(productId),
      getReviewEligibility(productId),
    ]);
    if (listRes.success) setReviews(listRes.reviews);
    if (eligRes.success) {
      setEligibility(eligRes.eligibility);
      const first = eligRes.eligibility?.items?.[0];
      setOrderItemId(first ? String(first.order_item_id) : "");
    }
    setLoading(false);
  }, [productId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let alive = true;
    LoyaltyService.getConfig().then((res) => { if (alive && res.success) setCoinsConfig(res.data); });
    return () => { alive = false; };
  }, []);

  const canWriteReview = Boolean(eligibility?.can_review);
  const items = eligibility?.items || [];
  const reasonKey = eligibility ? (eligibility.reason || "can_review") : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!canWriteReview) {
      setFormError(t(`reviews.eligibility.${reasonKey || "not_delivered"}`));
      return;
    }

    if (!AuthService.isAuthenticated()) {
      window.dispatchEvent(new CustomEvent("zaynic:open-auth", { detail: { tab: "login" } }));
      return;
    }

    if (!formComment.trim() && !formTitle.trim()) {
      setFormError(t("reviews.form.needText"));
      return;
    }

    setSubmitting(true);
    const res = await submitProductReview(productId, {
      rating: formRating,
      title: formTitle.trim(),
      comment: formComment.trim(),
      orderItemId: orderItemId ? Number(orderItemId) : undefined,
      mediaIds: media.map((m) => m.id),
    });
    setSubmitting(false);

    if (!res.success) {
      setFormError(typeof res.error === "string" ? res.error : t("reviews.errors.submit"));
      return;
    }

    setSubmittedStatus(res.review?.status || "PUBLISHED"); // i18n-ignore: status code
    setFormTitle("");
    setFormComment("");
    setFormRating(5);
    setMedia([]);
    await load();
    onReviewSubmitted?.();
  };

  const displayCount = Math.max(reviewCount ?? 0, reviews.length);
  const isLoggedIn = AuthService.isAuthenticated();
  const bonusText = coinsConfig?.enabled
    ? t("reviews.form.bonus", {
      text: coinsConfig.review_bonus_text,
      photos: coinsConfig.review_bonus_photos,
      video: coinsConfig.review_bonus_video,
    })
    : "";

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-sky-200 bg-sky-50/80 p-4">
        <p className="text-sm font-semibold text-slate-900">{t("reviews.intro.title")}</p>
        <p className="mt-1 text-sm text-slate-700">{t("reviews.intro.body")}</p>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">{t("reviews.loading")}</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            {ratingValue > 0 ? (
              <p className="text-sm font-semibold text-slate-900">{t("reviews.outOf5", { value: ratingValue })}</p>
            ) : null}
            <p className="text-sm text-slate-500">
              {t(displayCount === 1 ? "reviews.countOne" : "reviews.countMany", { count: displayCount })}
            </p>
          </div>

          <ReviewSummaryCard productId={productId} />

          {submittedStatus === "PENDING" && (
            <p className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900" role="status">
              {t("reviews.form.pendingNotice")}
            </p>
          )}
          {submittedStatus === "PUBLISHED" && (
            <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" role="status">
              {t("reviews.form.publishedNotice")}
            </p>
          )}

          {reasonKey && !submittedStatus && (
            <p
              className={`rounded-xl border px-4 py-3 text-sm ${
                canWriteReview
                  ? "border-amber-200 bg-amber-50 text-amber-950"
                  : "border-slate-200 bg-slate-50 text-slate-700"
              }`}
              role="status"
            >
              {t(`reviews.eligibility.${reasonKey}`)}
            </p>
          )}

          {canWriteReview && (
            <form
              onSubmit={handleSubmit}
              className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4 space-y-3"
              data-testid="review-form"
            >
              <p className="text-sm font-semibold text-slate-900">{t("reviews.form.title")}</p>
              <p className="text-xs text-slate-600">{t("reviews.form.verifiedOnly")}</p>
              {bonusText && <p className="text-xs font-medium text-amber-900">{bonusText}</p>}
              {items.length > 1 && (
                <label className="block text-xs font-semibold text-slate-800">
                  {t("reviews.form.whichOrder")}
                  <select
                    value={orderItemId}
                    onChange={(e) => setOrderItemId(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                  >
                    {items.map((it) => (
                      <option key={it.order_item_id} value={it.order_item_id}>
                        {t("reviews.form.orderOption", {
                          order: it.order_number,
                          date: new Date(it.delivered_at).toLocaleDateString(),
                        })}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <StarPicker value={formRating} onChange={setFormRating} />
              <input
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder={t("reviews.form.titlePlaceholder")}
                maxLength={255}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-200"
              />
              <textarea
                value={formComment}
                onChange={(e) => setFormComment(e.target.value)}
                placeholder={t("reviews.form.commentPlaceholder")}
                rows={3}
                maxLength={2000}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-200"
              />
              <MediaPicker media={media} setMedia={setMedia} disabled={submitting} />
              {media.length > 0 && <p className="text-xs text-slate-600">{t("reviews.form.mediaQueued")}</p>}
              {formError && <p className="text-xs text-rose-600">{formError}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="rounded-full bg-gradient-to-r from-orange-500 to-orange-400 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:from-orange-600 hover:to-orange-500 disabled:opacity-60"
              >
                {submitting ? t("reviews.form.submitting") : t("reviews.form.submit")}
              </button>
            </form>
          )}

          {!canWriteReview && !isLoggedIn && (
            <p className="text-sm text-slate-600">
              <button
                type="button"
                className="font-semibold text-primary-ink hover:underline"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent("zaynic:open-auth", { detail: { tab: "login" } }))
                }
              >
                {t("reviews.signIn")}
              </button>{" "}
              {t("reviews.signInSuffix")}
            </p>
          )}

          {!canWriteReview && isLoggedIn && !eligibility?.has_purchased && (
            <p className="text-sm text-slate-600">
              {t("reviews.notPurchased")}{" "}
              <Link to="/shop" className="font-semibold text-primary-ink hover:underline">
                {t("reviews.shop")}
              </Link>
            </p>
          )}

          <div className="space-y-3">
            <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">
              {t("reviews.listTitle")}
            </h3>
            {reviews.length === 0 ? (
              <p className="text-sm text-slate-500">{t("reviews.none")}</p>
            ) : (
              reviews.map((r) => (
                <article
                  key={r.id}
                  className="rounded-2xl border border-slate-200 bg-white p-4"
                  data-testid="review-card"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900">{r.author_name}</p>
                      {r.verified_purchase && <Badge>✓ {t("reviews.badges.verified")}</Badge>}
                      {(r.has_photos || r.has_video) && (
                        <Badge tone="info">
                          {t(r.has_video ? (r.has_photos ? "reviews.badges.photosVideo" : "reviews.badges.video")
                            : "reviews.badges.photos")}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-amber-600">
                      <span aria-hidden>{"★".repeat(Math.round(r.rating))}</span>
                      <span className="text-slate-500">{Number(r.rating).toFixed(1)}</span>
                    </div>
                  </div>
                  {r.title && <p className="mt-1 text-sm font-medium text-slate-800">{r.title}</p>}
                  {r.comment && (
                    <p className="mt-2 text-sm text-slate-600 leading-7">&ldquo;{r.comment}&rdquo;</p>
                  )}
                  {r.media?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {r.media.map((m) => (m.kind === "video" ? (
                        <video key={m.id} src={m.url} controls preload="metadata"
                          className="h-28 w-40 rounded-lg border border-slate-200 bg-black object-cover" />
                      ) : (
                        <a key={m.id} href={m.url} target="_blank" rel="noopener noreferrer">
                          <img src={m.url} alt={t("reviews.media.photoAlt")} loading="lazy"
                            className="h-20 w-20 rounded-lg border border-slate-200 object-cover" />
                        </a>
                      )))}
                    </div>
                  )}
                  {r.vendor_reply && (
                    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      <p className="text-xs font-semibold text-slate-800">{t("reviews.sellerReply")}</p>
                      <p className="mt-1 text-sm text-slate-700">{r.vendor_reply}</p>
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    {r.created_at && (
                      <p className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</p>
                    )}
                    <ReportButton reviewId={r.id} />
                  </div>
                </article>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
